import { useState } from 'react'

import { ConnectGitHubGate } from '@/components/integration/ConnectGitHubGate'
import { RepoCard } from '@/components/repositories/RepoCard'
import { Button } from '@/components/ui/Controls'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { EmptyState, QueryBoundary, Skeleton } from '@/components/ui/States'
import { useRepositories } from '@/features/repositories/useRepositories'

export function RepositoriesPage() {
  const [includeArchived, setIncludeArchived] = useState(false)
  const { data, isLoading, error, refetch } = useRepositories(includeArchived)
  const repos = data?.items ?? []

  return (
    <div>
      <PageHeader
        title="Repositories"
        description="Every repository your connected GitHub account can reach — your own, your organisations', and ones you collaborate on. The browser never talks to GitHub; every field here came through the API."
        actions={
          <Button size="sm" onClick={() => setIncludeArchived((value) => !value)}>
            {includeArchived ? 'Hide archived' : 'Show archived'}
          </Button>
        }
      />

      <ConnectGitHubGate what="Your repositories" error={error}>
      <Panel
        title="Connected repositories"
        description={data ? `${data.total} accessible to your GitHub account` : undefined}
        actions={
          data?.truncated ? (
            <span className="text-xs text-waiting" title="A server-side scan limit was reached">
              partial list
            </span>
          ) : null
        }
      >
        <QueryBoundary
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          isEmpty={repos.length === 0}
          loadingFallback={
            <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="m-4 h-24" />
              ))}
            </div>
          }
          emptyState={
            <EmptyState
              title="No repositories are visible."
              hint="Your connected GitHub account cannot see any repositories — its own or an organisation's. If you expect an organisation's repositories here, check that the organisation allows this app."
            />
          }
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3">
            {repos.map((repo) => (
              <RepoCard key={repo.full_name} repo={repo} />
            ))}
          </div>
        </QueryBoundary>
      </Panel>
      </ConnectGitHubGate>
    </div>
  )
}
